import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TenantSignup } from '@hospasense/contracts';
import { useSignups } from '../lib/queries.js';
import { useAuth } from '../lib/auth.js';
import { formatDateTime, relativeTime } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  PermissionDeniedState,
  Skeleton,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui/index.js';
import { SignupDecisionDialog } from './signup-decision-dialog.js';

/**
 * The pending-signup queue.
 *
 * The screen the owner went looking for and did not find. Three hospitals had
 * registered since 2026-08-28 and Control had never heard of any of them.
 *
 * DESIGN RULE THROUGHOUT THIS PAGE: every number is labelled with where it came
 * from. Bed counts and OPD volumes are what a prospect TYPED INTO A PUBLIC FORM
 * — UniSoft GmbH declared 120 beds and one member of staff, which is not a
 * hospital. Presenting those as measurements would put fiction on a console
 * whose whole purpose is being trustworthy, so they are rendered as "declared"
 * and never totalled.
 */

const STATUS_TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
] as const;

function StatusBadge({ status }: { status: TenantSignup['status'] }) {
  if (status === 'approved') return <Badge tone="success">Approved</Badge>;
  if (status === 'rejected') return <Badge tone="neutral">Rejected</Badge>;
  return <Badge tone="warning">Pending</Badge>;
}

/**
 * How long this hospital has been waiting.
 *
 * Shown against `requestedAt` — when they signed up — NOT `receivedAt`, when
 * Control heard about it. For the three hospitals that arrive by backfill those
 * differ by weeks, and the honest number is the one that says how long somebody
 * has actually been waiting for an answer.
 */
function WaitingFor({ signup }: { signup: TenantSignup }) {
  if (signup.status !== 'pending') return <span className="text-text-muted">—</span>;
  const days = Math.floor((Date.now() - new Date(signup.requestedAt).getTime()) / 86_400_000);
  if (days >= 7) {
    return <Badge tone="error">{`waiting ${days} days`}</Badge>;
  }
  if (days >= 2) {
    return <Badge tone="warning">{`waiting ${days} days`}</Badge>;
  }
  return <span>{relativeTime(signup.requestedAt)}</span>;
}

export function SignupsPage() {
  const { hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const [deciding, setDeciding] = useState<{
    signup: TenantSignup;
    mode: 'approve' | 'reject';
  } | null>(null);

  const status = params.get('status') ?? 'pending';
  /**
   * The default must reach the QUERY, not just the tab highlight.
   *
   * Passing `params` straight through sent no status at all on first load, so
   * the API returned every signup while the Pending tab sat highlighted above
   * them — the operator's outstanding workload, misstated, on the one screen
   * whose job is to be trusted.
   */
  const effectiveParams = new URLSearchParams(params);
  if (!effectiveParams.has('status') && status) effectiveParams.set('status', status);
  const query = useSignups(effectiveParams);

  const setParam = useCallback(
    (key: string, value: string): void => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  if (!hasPermission('signup.read')) {
    return <PermissionDeniedState what="hospital signups" />;
  }

  const canDecide = hasPermission('signup.decide');
  const data = query.data;

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs items={[{ label: 'Signups' }]} />
      <PageHeader
        title="Hospital signups"
        subtitle="Registrations arriving from the HospaSense tenant platform."
      />

      <div className="flex gap-1 border-b border-border" role="tablist">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key || 'all'}
            role="tab"
            type="button"
            aria-selected={status === tab.key}
            className={
              status === tab.key
                ? 'border-b-2 border-accent px-3 py-2 text-sm font-medium text-text-primary'
                : 'border-b-2 border-transparent px-3 py-2 text-sm text-text-muted hover:text-text-secondary'
            }
            onClick={() => setParam('status', tab.key)}
          >
            {tab.label}
            {tab.key === 'pending' && data && data.pendingTotal > 0 ? (
              <span className="ml-1.5 rounded-full bg-accent/15 px-1.5 py-0.5 text-xs text-accent">
                {data.pendingTotal}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {query.isLoading ? <Skeleton rows={5} /> : null}
      {query.isError ? (
        <ErrorState
          description={(query.error as Error).message}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {data && data.items.length === 0 ? (
        <EmptyState
          title={status === 'pending' ? 'No signups waiting' : 'Nothing here'}
          /**
           * Says WHY it is empty and how to tell a healthy silence from a broken
           * pipeline. An empty state that just says "no data" is exactly how
           * this system hid three missing hospitals for a fortnight.
           */
          description={
            status === 'pending'
              ? 'Nothing is waiting for a decision. New registrations appear here within a minute of a hospital signing up on the tenant platform.'
              : 'No signups match this filter.'
          }
        />
      ) : null}

      {data && data.items.length > 0 ? (
        <TableContainer>
          <Table>
            <THead>
              <TR>
                <TH>Hospital</TH>
                <TH>Contact</TH>
                <TH>Declared size</TH>
                <TH>Signed up</TH>
                <TH>Status</TH>
                <TH>Waiting</TH>
                {canDecide ? <TH>Decision</TH> : null}
              </TR>
            </THead>
            <TBody>
              {data.items.map((s) => (
                <TR key={s.id}>
                  <TD>
                    <div className="flex flex-col gap-0.5">
                      <strong>{s.organisationName}</strong>
                      <span className="text-xs text-text-muted">
                        {s.organisationType} · {s.deploymentPreference.replace(/_/g, ' ')}
                        {s.ingestSource === 'backfill' ? ' · backfilled' : ''}
                      </span>
                    </div>
                  </TD>
                  <TD>
                    <div className="flex flex-col gap-0.5">
                      <span>{s.contactName}</span>
                      <a href={`mailto:${s.contactEmail}`} className="text-xs text-text-muted">
                        {s.contactEmail}
                      </a>
                    </div>
                  </TD>
                  <TD>
                    {/*
                      "Declared", every time. These are form entries, not
                      measurements, and the moment they are shown as plain
                      numbers somebody will add them up.
                    */}
                    <span className="text-xs text-text-muted">
                      {s.declaredBedCount === null
                        ? 'beds not stated'
                        : `${s.declaredBedCount} beds declared`}
                      {s.declaredDailyOpdVolume !== null
                        ? ` · ${s.declaredDailyOpdVolume} OPD/day declared`
                        : ''}
                    </span>
                  </TD>
                  <TD>
                    <div className="flex flex-col gap-0.5">
                      <span title={formatDateTime(s.requestedAt)}>
                        {formatDateTime(s.requestedAt)}
                      </span>
                      {/*
                        The lag between signing up and Control hearing about it.
                        Shown only when it is more than a minute, because for a
                        live pipeline it is noise — and for a backfilled row it
                        is the whole story.
                      */}
                      {new Date(s.receivedAt).getTime() - new Date(s.requestedAt).getTime() >
                      60_000 ? (
                        <span className="text-xs text-text-muted">
                          {`Control learned ${relativeTime(s.receivedAt)}`}
                        </span>
                      ) : null}
                    </div>
                  </TD>
                  <TD>
                    <div className="flex flex-col gap-0.5">
                      <StatusBadge status={s.status} />
                      {s.decidedByDisplayName ? (
                        <span className="text-xs text-text-muted">by {s.decidedByDisplayName}</span>
                      ) : null}
                    </div>
                  </TD>
                  <TD>
                    <WaitingFor signup={s} />
                  </TD>
                  {canDecide ? (
                    <TD>
                      {s.status === 'pending' ? (
                        <div className="flex gap-2">
                          <Button
                            variant="primary"
                            onClick={() => setDeciding({ signup: s, mode: 'approve' })}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setDeciding({ signup: s, mode: 'reject' })}
                          >
                            Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-text-muted">
                          {s.decidedAt ? formatDateTime(s.decidedAt) : '—'}
                        </span>
                      )}
                    </TD>
                  ) : null}
                </TR>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      ) : null}

      {deciding ? (
        <SignupDecisionDialog
          signup={deciding.signup}
          mode={deciding.mode}
          onClose={() => setDeciding(null)}
        />
      ) : null}
    </div>
  );
}
